/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `magnetometer_data` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class MagnetometerDataMesg : Mesg {
    public constructor() : super("MagnetometerData", Profile.MesgNum.MAGNETOMETER_DATA)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. Whole second part of the timestamp */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** timestamp_ms, in ms. Millisecond part of the timestamp. */
    public var timestampMs: UShort?
        get() = getFieldValue(TIMESTAMP_MS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TIMESTAMP_MS_FIELD_NUM, value)

    /** Number of values stored in sample_time_offset. */
    public val numSampleTimeOffset: Int
        get() = getField(SAMPLE_TIME_OFFSET_FIELD_NUM)?.numValues ?: 0

    /** sample_time_offset, in ms. Each time in the array describes the time at which the compass sample with the corrosponding index was taken. Limited to 30 samples in each message. The samples may span across seconds. Array size must match the number of samples in cmps_x and cmps_y and cmps_z */
    public var sampleTimeOffset: List<UShort?>?
        get() {
            val field = getField(SAMPLE_TIME_OFFSET_FIELD_NUM) ?: return null
            return List(field.numValues) { getSampleTimeOffset(it) }
        }
        set(value) {
            removeField(SAMPLE_TIME_OFFSET_FIELD_NUM)
            value?.forEachIndexed { index, element -> setSampleTimeOffset(index, element) }
        }

    public fun getSampleTimeOffset(index: Int): UShort? =
        getFieldValue(SAMPLE_TIME_OFFSET_FIELD_NUM, index) as? UShort

    public fun setSampleTimeOffset(index: Int, value: UShort?) {
        setFieldValue(SAMPLE_TIME_OFFSET_FIELD_NUM, value, index)
    }

    /** Number of values stored in mag_x. */
    public val numMagX: Int
        get() = getField(MAG_X_FIELD_NUM)?.numValues ?: 0

    /** mag_x, in counts. These are the raw ADC reading. Maximum number of samples is 30 in each message. The samples may span across seconds. A conversion will need to be done on this data once read. */
    public var magX: List<UShort?>?
        get() {
            val field = getField(MAG_X_FIELD_NUM) ?: return null
            return List(field.numValues) { getMagX(it) }
        }
        set(value) {
            removeField(MAG_X_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMagX(index, element) }
        }

    public fun getMagX(index: Int): UShort? =
        getFieldValue(MAG_X_FIELD_NUM, index) as? UShort

    public fun setMagX(index: Int, value: UShort?) {
        setFieldValue(MAG_X_FIELD_NUM, value, index)
    }

    /** Number of values stored in mag_y. */
    public val numMagY: Int
        get() = getField(MAG_Y_FIELD_NUM)?.numValues ?: 0

    /** mag_y, in counts. These are the raw ADC reading. Maximum number of samples is 30 in each message. The samples may span across seconds. A conversion will need to be done on this data once read. */
    public var magY: List<UShort?>?
        get() {
            val field = getField(MAG_Y_FIELD_NUM) ?: return null
            return List(field.numValues) { getMagY(it) }
        }
        set(value) {
            removeField(MAG_Y_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMagY(index, element) }
        }

    public fun getMagY(index: Int): UShort? =
        getFieldValue(MAG_Y_FIELD_NUM, index) as? UShort

    public fun setMagY(index: Int, value: UShort?) {
        setFieldValue(MAG_Y_FIELD_NUM, value, index)
    }

    /** Number of values stored in mag_z. */
    public val numMagZ: Int
        get() = getField(MAG_Z_FIELD_NUM)?.numValues ?: 0

    /** mag_z, in counts. These are the raw ADC reading. Maximum number of samples is 30 in each message. The samples may span across seconds. A conversion will need to be done on this data once read. */
    public var magZ: List<UShort?>?
        get() {
            val field = getField(MAG_Z_FIELD_NUM) ?: return null
            return List(field.numValues) { getMagZ(it) }
        }
        set(value) {
            removeField(MAG_Z_FIELD_NUM)
            value?.forEachIndexed { index, element -> setMagZ(index, element) }
        }

    public fun getMagZ(index: Int): UShort? =
        getFieldValue(MAG_Z_FIELD_NUM, index) as? UShort

    public fun setMagZ(index: Int, value: UShort?) {
        setFieldValue(MAG_Z_FIELD_NUM, value, index)
    }

    /** Number of values stored in calibrated_mag_x. */
    public val numCalibratedMagX: Int
        get() = getField(CALIBRATED_MAG_X_FIELD_NUM)?.numValues ?: 0

    /** calibrated_mag_x, in G. Calibrated Magnetometer reading */
    public var calibratedMagX: List<Float?>?
        get() {
            val field = getField(CALIBRATED_MAG_X_FIELD_NUM) ?: return null
            return List(field.numValues) { getCalibratedMagX(it) }
        }
        set(value) {
            removeField(CALIBRATED_MAG_X_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCalibratedMagX(index, element) }
        }

    public fun getCalibratedMagX(index: Int): Float? =
        getFieldValue(CALIBRATED_MAG_X_FIELD_NUM, index) as? Float

    public fun setCalibratedMagX(index: Int, value: Float?) {
        setFieldValue(CALIBRATED_MAG_X_FIELD_NUM, value, index)
    }

    /** Number of values stored in calibrated_mag_y. */
    public val numCalibratedMagY: Int
        get() = getField(CALIBRATED_MAG_Y_FIELD_NUM)?.numValues ?: 0

    /** calibrated_mag_y, in G. Calibrated Magnetometer reading */
    public var calibratedMagY: List<Float?>?
        get() {
            val field = getField(CALIBRATED_MAG_Y_FIELD_NUM) ?: return null
            return List(field.numValues) { getCalibratedMagY(it) }
        }
        set(value) {
            removeField(CALIBRATED_MAG_Y_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCalibratedMagY(index, element) }
        }

    public fun getCalibratedMagY(index: Int): Float? =
        getFieldValue(CALIBRATED_MAG_Y_FIELD_NUM, index) as? Float

    public fun setCalibratedMagY(index: Int, value: Float?) {
        setFieldValue(CALIBRATED_MAG_Y_FIELD_NUM, value, index)
    }

    /** Number of values stored in calibrated_mag_z. */
    public val numCalibratedMagZ: Int
        get() = getField(CALIBRATED_MAG_Z_FIELD_NUM)?.numValues ?: 0

    /** calibrated_mag_z, in G. Calibrated Magnetometer reading */
    public var calibratedMagZ: List<Float?>?
        get() {
            val field = getField(CALIBRATED_MAG_Z_FIELD_NUM) ?: return null
            return List(field.numValues) { getCalibratedMagZ(it) }
        }
        set(value) {
            removeField(CALIBRATED_MAG_Z_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCalibratedMagZ(index, element) }
        }

    public fun getCalibratedMagZ(index: Int): Float? =
        getFieldValue(CALIBRATED_MAG_Z_FIELD_NUM, index) as? Float

    public fun setCalibratedMagZ(index: Int, value: Float?) {
        setFieldValue(CALIBRATED_MAG_Z_FIELD_NUM, value, index)
    }

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val TIMESTAMP_MS_FIELD_NUM: UByte = 0u
        public const val SAMPLE_TIME_OFFSET_FIELD_NUM: UByte = 1u
        public const val MAG_X_FIELD_NUM: UByte = 2u
        public const val MAG_Y_FIELD_NUM: UByte = 3u
        public const val MAG_Z_FIELD_NUM: UByte = 4u
        public const val CALIBRATED_MAG_X_FIELD_NUM: UByte = 5u
        public const val CALIBRATED_MAG_Y_FIELD_NUM: UByte = 6u
        public const val CALIBRATED_MAG_Z_FIELD_NUM: UByte = 7u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("TimestampMs", 0u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[1] = Field("SampleTimeOffset", 1u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[2] = Field("MagX", 2u, BaseType.UINT16, 1.0, 0.0, "counts", false)
            fields[3] = Field("MagY", 3u, BaseType.UINT16, 1.0, 0.0, "counts", false)
            fields[4] = Field("MagZ", 4u, BaseType.UINT16, 1.0, 0.0, "counts", false)
            fields[5] = Field("CalibratedMagX", 5u, BaseType.FLOAT32, 1.0, 0.0, "G", false)
            fields[6] = Field("CalibratedMagY", 6u, BaseType.FLOAT32, 1.0, 0.0, "G", false)
            fields[7] = Field("CalibratedMagZ", 7u, BaseType.FLOAT32, 1.0, 0.0, "G", false)
            return fields
        }
    }
}
